import React, {
  useState,
  useEffect,
  useCallback,
  memo,
  useRef,
  useMemo,
} from "react";
import {
  SectionList,
  StyleSheet,
  View,
  TouchableWithoutFeedback,
  TouchableOpacity,
} from "react-native";
import { getRecentLedgerTxnsv2 } from "@services/api";
import PaymentCard from "@screens/MyDeliveries/DeliveryReturn/PaymentCard";
import HeaderWithBackAndTitle from "@components/HeaderWithBackAndTitle";
import { useFocusEffect, useIsFocused } from "@react-navigation/native";
import UnsuccessfulTxnSvg from "@assets/svg/UnsuccessfulLedgerTxn";
import HtmlText from "./HtmlText";
import ItemDetailsModal from "./ItemDetailsModal";
import DateFilterModal from "./DateFilterModal";
import Filter from "@assets/svg/LedgerTxns/Filter";
import { colors } from "@constants/theme";
import Text from "@components/CustomText";
import { useWebSocketEvents } from "@hooks/useWebSocketEvents";

const UnsuccessfulTxn = ({ status }) => {
  return (
    <View
      style={{
        flexDirection: "row",
        alignItems: "stretch",
        justifyContent: "center",
        marginVertical: 2,
      }}
    >
      <View
        style={{
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <UnsuccessfulTxnSvg status={status} />
      </View>
      <Text
        style={{
          fontSize: 12,
          marginLeft: 4,
          fontWeight: "bold",
          color: status === "FAILED" ? "#FF2E00" : "#FFC000",
        }}
      >
        {`${status[0]}${status.slice(1).toLowerCase()}`}
      </Text>
    </View>
  );
};

const MemoizedListItem = memo(({ item, onPress, onLayout, isFirst }) => {
  const renderItem = (
    <View style={styles.listItem} onLayout={isFirst ? onLayout : undefined}>
      <View style={styles.itemTextContainer}>
        <Text style={styles.typeText}>{item?.type_txt || ""}</Text>
        <Text style={styles.dateText}>{item?.created_at_txt || ""}</Text>
        {item?.modal_api ? (
          <View style={styles.seeItemsContainer}>
            <Text style={styles.seeItemsText}>{item.modal_txt || ""}</Text>
            <Text style={styles.arrowText}> ›</Text>
          </View>
        ) : null}
      </View>
      <View style={styles.amountContainer}>
        <Text
          style={
            item?.type === "UNSUCCESSFUL"
              ? styles.unsuccessfulAmount
              : item?.type === "CREDIT"
                ? styles.creditAmount
                : styles.debitAmount
          }
        >
          {item?.amount_txt || ""}
        </Text>
        {item?.type === "UNSUCCESSFUL" ? (
          <UnsuccessfulTxn status={item?.status} />
        ) : (
          <HtmlText text={item?.net_pending_txt || ""} />
        )}
      </View>
    </View>
  );

  if (item?.modal_api) {
    return (
      <TouchableWithoutFeedback onPress={onPress}>
        {renderItem}
      </TouchableWithoutFeedback>
    );
  }
  return renderItem;
});

const LedgerTxns = ({ navigation, route }) => {
  const [txnsData, setTxnsData] = useState(null);
  const ledger = txnsData?.ledger_summary || route?.params?.ledger;
  const [itemDetailsModalVisible, setItemDetailsModalVisible] = useState(false);
  const [itemDetailsModalData, setItemDetailsModalData] = useState({});
  const [itemHeight, setItemHeight] = useState(70); // Default height
  const firstItemMeasured = useRef(false);
  const [dateFilterModalVisible, setDateFilterModalVisible] = useState(false);
  const [dateFilter, setDateFilter] = useState({
    startDate: null,
    endDate: null,
  });

  const isFocused = useIsFocused();
  useEffect(() => {
    fetchData();
  }, [dateFilter]);

  const { lastMessage } = useWebSocketEvents({});

  useEffect(() => {
    if (lastMessage !== null) {
      try {
        const event = JSON.parse(lastMessage.data);
        if (event.type === "REFRESH_OVERVIEW_PAGE") {
          fetchData();
        }
      } catch (error) {
        console.error("Error processing WebSocket message:", error);
      }
    }
  }, [lastMessage]);

  const fetchData = async () => {
    try {
      const payload = {
        filter: {
          startDate: dateFilter.startDate,
          endDate: dateFilter.endDate,
        },
      };
      const { data } = await getRecentLedgerTxnsv2({ payload });
      setTxnsData(data);
    } catch (error) {
      console.error(error);
    }
  };

  useFocusEffect(
    useCallback(() => {
      if (isFocused) {
        fetchData();
      }
    }, [isFocused]),
  );

  const handleItemLayout = useCallback((event) => {
    if (!firstItemMeasured.current) {
      const { height } = event.nativeEvent.layout;
      if (height > 0) {
        setItemHeight(height);
        firstItemMeasured.current = true;
      }
    }
  }, []);

  const renderSectionHeader = useCallback(({ section }) => {
    return (
      <View style={styles.sectionHeader}>
        <Text variant="caption_1" style={styles.sectionHeaderText}>
          {section.title}
        </Text>
        <Text variant="caption_3" style={styles.netPendingText}>
          {`(Net pending `}
          <Text variant="caption_3" style={styles.netPendingTextValue}>
            {`${section.net_pending})`}
          </Text>
        </Text>
      </View>
    );
  }, []);

  const renderItemWithTap = useCallback(
    ({ item, index }) => {
      return (
        <MemoizedListItem
          item={item}
          onPress={() => {
            if (item?.modal_api) {
              setItemDetailsModalData(item);
              setItemDetailsModalVisible(true);
            }
          }}
          onLayout={handleItemLayout}
          isFirst={index === 0}
        />
      );
    },
    [handleItemLayout],
  );

  const keyExtractor = useCallback((item) => item.id, []);

  const sections = useMemo(() => {
    return (txnsData?.txns || []).map((txn) => ({
      title: txn.date,
      net_pending: txn.net_pending,
      data: txn.data,
    }));
  }, [txnsData?.txns]);

  const isFilterApplied = () => {
    return dateFilter.startDate || dateFilter.endDate;
  };
  const applyDateFilter = (startDate, endDate) => {
    setDateFilter({
      startDate,
      endDate,
    });
    setDateFilterModalVisible(false);
  };

  const listHeaderComponent = (
    <>
      <PaymentCard ledger={ledger} />
      <View style={styles.listHeaderContainer}>
        <Text style={styles.listHeaderTitle}>{txnsData?.title || ""}</Text>
        <View style={styles.subtitleContainer}>
          <Text variant="caption_2">{txnsData?.filterTitle || ""}</Text>
          <TouchableOpacity
            testID="ledger-filter"
            onPress={() => setDateFilterModalVisible(true)}
            activeOpacity={0.7}
            style={[
              styles.filterContainer,
              isFilterApplied() && styles.filterContainerActive,
            ]}
          >
            {isFilterApplied() && <View style={styles.filterBadge} />}
            <Filter />
            <Text variant="body_text_3" style={styles.filterText}>
              Filter
            </Text>
          </TouchableOpacity>
        </View>
      </View>
    </>
  );

  const listFooterComponent = () => {
    return <View style={styles.listfooterContainer} />;
  };

  const emptyListComponent = () => {
    return (
      <View style={styles.emptyContainer}>
        <Text variant="title_18" style={styles.emptyText}>
          No Transactions Found
        </Text>
      </View>
    );
  };

  return (
    <View style={styles.container}>
      <HeaderWithBackAndTitle
        headertitle={txnsData?.header_txt || ""}
        headerColor="#fff"
        hideShadow={false}
        navigation={navigation}
        route={route}
      />
      <SectionList
        sections={sections}
        renderItem={renderItemWithTap}
        renderSectionHeader={renderSectionHeader}
        ListHeaderComponent={listHeaderComponent}
        keyExtractor={keyExtractor}
        ListFooterComponent={listFooterComponent}
        maxToRenderPerBatch={10}
        initialNumToRender={10}
        style={styles.listContainer}
        ListEmptyComponent={emptyListComponent}
      />
      <ItemDetailsModal
        itemDetailsModalVisible={itemDetailsModalVisible}
        setItemDetailsModalVisible={setItemDetailsModalVisible}
        itemDetailsModalData={itemDetailsModalData}
      />
      <DateFilterModal
        visible={dateFilterModalVisible}
        onApply={applyDateFilter}
        onClose={() => setDateFilterModalVisible(false)}
        days_limit={txnsData?.days_limit}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#fff",
  },
  listItem: {
    flexDirection: "row",
    marginHorizontal: 16,
    paddingHorizontal: 16,
    paddingVertical: 10,
    backgroundColor: "rgba(248, 248, 248, 1)",
    borderRadius: 12,
    marginBottom: 8,
    alignItems: "center",
  },
  itemTextContainer: {
    flex: 4,
    flexDirection: "column",
  },
  typeText: {
    fontSize: 14,
  },
  dateText: {
    color: "rgba(0, 0, 0, 0.56)",
    fontSize: 10,
  },
  seeItemsText: {
    color: "#E41E31",
    fontSize: 14,
    fontWeight: "500",
  },
  arrowText: {
    color: "#E41E31",
    fontSize: 18,
    fontWeight: "bold",
    marginLeft: 2,
  },
  amountContainer: {
    flex: 3,
    alignItems: "flex-end",
  },
  creditAmount: {
    color: "rgba(228, 0, 70, 1)",
    fontSize: 16,
    fontWeight: "700",
  },
  unsuccessfulAmount: {
    color: "black",
    fontSize: 16,
    fontWeight: "700",
  },
  debitAmount: {
    color: "rgba(0, 171, 89, 1)",
    fontSize: 16,
    fontWeight: "700",
  },
  listHeaderContainer: {
    marginTop: 4,
    marginHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: "#fff",
    justifyContent: "space-between",
    flexDirection: "row",
    paddingBottom: 8,
  },
  listHeaderTitle: {
    fontWeight: "bold",
    fontSize: 16,
  },
  subtitleContainer: {
    flexDirection: "row",
    alignItems: "center",
  },
  filterContainer: {
    flexDirection: "row",
    alignItems: "center",
    borderWidth: 1,
    borderColor: colors.borderColor3,
    backgroundColor: colors.lightGrayWhite,
    borderRadius: 6,
    paddingHorizontal: 4,
    paddingVertical: 2,
    paddingRight: 8,
    marginLeft: 4,
  },
  filterBadge: {
    backgroundColor: colors.mediumBlack,
    padding: 2,
    borderRadius: 20,
    position: "absolute",
    top: -6,
    right: -4,
    minWidth: 10,
    height: 10,
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 1,
    borderColor: colors.white,
  },
  filterText: {
    marginLeft: 2,
  },
  listfooterContainer: {
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
    marginBottom: 10,
    marginHorizontal: 16,
    paddingHorizontal: 16,
    paddingBottom: 12,
    backgroundColor: "#fff",
  },
  seeItemsContainer: {
    flexDirection: "row",
    alignItems: "center",
  },
  sectionHeader: {
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "center",
    paddingHorizontal: 16,
    marginBottom: 8,
    marginTop: 4,
    backgroundColor: colors.white,
    textAlign: "center",
  },
  sectionHeaderText: {
    color: colors.mediumBlacklightBlack,
  },
  netPendingText: {
    color: colors.mediumBlack,
    lineHeight: 21,
    marginLeft: 4,
  },
  netPendingTextValue: {
    color: colors.mediumBlack,
    lineHeight: 21,
    fontWeight: "700",
  },
  listContainer: {
    flex: 1,
  },
  filterContainerActive: {
    backgroundColor: colors.lightGrayWhite,
    borderWidth: 1,
    borderColor: colors.mediumBlack,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    marginTop: "40%",
  },
  emptyText: {
    color: colors.mediumBlacklightBlack,
    textAlign: "center",
  },
});

export default LedgerTxns;
